import { useState, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { X, Trash2, ImageIcon } from 'lucide-react';
import { cn } from '../../ui/cn.ts';
import { api } from '../../../lib/api.ts';
import { MediaPickerModal } from '../../ui/MediaPickerModal.tsx';
import { SimpleRichTextEditor } from './SimpleRichTextEditor.tsx';
import { VariablePicker } from './VariablePicker.tsx';

// ─── Types ─────────────────────────────────────────────────────────────────────

interface FieldDef {
  key: string;
  label: string;
  type: string;
  required?: boolean;
  options?: string[];
  collectionKey?: string;
}

interface BlockDef {
  id: string;
  name: string;
  slug: string;
  fieldsJson: FieldDef[];
}

interface StyleProps {
  paddingTop?: 'none' | 'sm' | 'md' | 'lg' | 'xl';
  paddingBottom?: 'none' | 'sm' | 'md' | 'lg' | 'xl';
  backgroundColor?: string;
  textColor?: string;
  maxWidth?: 'sm' | 'md' | 'lg' | 'full';
}

interface Props {
  blockId: string;
  blockType: string;
  blockDef: BlockDef | null;
  props: Record<string, unknown>;
  spaceId?: string;
  contextCollectionSlug?: string;
  onUpdate: (blockId: string, props: Record<string, unknown>) => void;
  onDelete: (blockId: string) => void;
  onClose: () => void;
}

type Tab = 'content' | 'style' | 'advanced';

// ─── Style field options ────────────────────────────────────────────────────────

const PADDING_OPTIONS = ['none', 'sm', 'md', 'lg', 'xl'] as const;
const MAX_WIDTH_OPTIONS = ['sm', 'md', 'lg', 'full'] as const;

// ─── Shared input styles (dark panel) ──────────────────────────────────────────

const labelCls = 'block text-[10px] font-medium uppercase tracking-wide text-gray-600 mb-1';
const inputCls =
  'w-full rounded-md border border-gray-300 bg-white px-2.5 py-1.5 text-xs text-gray-900 focus:border-zinc-500 focus:outline-none focus:ring-1 focus:ring-zinc-500';
const selectCls =
  'w-full rounded-md border border-gray-300 bg-white px-2.5 py-1.5 text-xs text-gray-900 focus:border-zinc-500 focus:outline-none';

// ─── Array item schemas for visual editor ────────────────────────────────────────

interface ItemField {
  key: string;
  label: string;
  type?: 'text' | 'select';
  options?: string[];
  placeholder?: string;
}

const ARRAY_ITEM_SCHEMAS: Record<string, { fields: ItemField[]; newItem: () => Record<string, unknown> }> = {
  items: {
    fields: [{ key: 'title', label: 'Title', placeholder: 'Section title' }],
    newItem: () => ({ title: '' }),
  },
  tabs: {
    fields: [{ key: 'label', label: 'Label', placeholder: 'Tab name' }],
    newItem: () => ({ label: '' }),
  },
  stats: {
    fields: [
      { key: 'value', label: 'Value', placeholder: '10K+' },
      { key: 'label', label: 'Label', placeholder: 'Users' },
      { key: 'prefix', label: 'Prefix', placeholder: '$' },
      { key: 'suffix', label: 'Suffix', placeholder: '+' },
    ],
    newItem: () => ({ value: '', label: '' }),
  },
  members: {
    fields: [
      { key: 'name', label: 'Name', placeholder: 'John Doe' },
      { key: 'role', label: 'Role', placeholder: 'CEO' },
      { key: 'imageUrl', label: 'Photo URL', placeholder: 'https://...' },
      { key: 'bio', label: 'Bio', placeholder: 'Short bio' },
    ],
    newItem: () => ({ name: '', role: '' }),
  },
  logos: {
    fields: [
      { key: 'imageUrl', label: 'Logo URL', placeholder: 'https://...' },
      { key: 'name', label: 'Name', placeholder: 'Company' },
      { key: 'link', label: 'Link', placeholder: 'https://...' },
    ],
    newItem: () => ({ imageUrl: '', name: '' }),
  },
  images: {
    fields: [
      { key: 'url', label: 'Image URL', placeholder: 'https://...' },
      { key: 'alt', label: 'Alt text', placeholder: 'Description' },
      { key: 'caption', label: 'Caption', placeholder: 'Optional caption' },
    ],
    newItem: () => ({ url: '', alt: '' }),
  },
  boxes: {
    fields: [
      { key: 'icon', label: 'Icon (emoji)', placeholder: '⭐' },
      { key: 'title', label: 'Title', placeholder: 'Feature' },
      { key: 'description', label: 'Description', placeholder: '...' },
    ],
    newItem: () => ({ icon: '', title: '', description: '' }),
  },
  buttons: {
    fields: [
      { key: 'label', label: 'Label', placeholder: 'Click me' },
      { key: 'url', label: 'URL', placeholder: '/page' },
      { key: 'variant', label: 'Style', type: 'select', options: ['primary', 'secondary', 'outline'] },
    ],
    newItem: () => ({ label: '', url: '', variant: 'primary' }),
  },
  links: {
    fields: [
      { key: 'platform', label: 'Platform', type: 'select', options: ['facebook', 'twitter', 'instagram', 'linkedin', 'youtube', 'tiktok'] },
      { key: 'url', label: 'URL', placeholder: 'https://...' },
    ],
    newItem: () => ({ platform: 'facebook', url: '' }),
  },
  testimonials: {
    fields: [
      { key: 'quote', label: 'Quote', placeholder: 'Great product!' },
      { key: 'author', label: 'Author', placeholder: 'Jane Doe' },
      { key: 'authorTitle', label: 'Title', placeholder: 'CEO at...' },
      { key: 'avatarUrl', label: 'Avatar URL', placeholder: 'https://...' },
    ],
    newItem: () => ({ quote: '', author: '' }),
  },
  features: {
    fields: [
      { key: 'icon', label: 'Icon', placeholder: '⚡' },
      { key: 'title', label: 'Title', placeholder: 'Feature' },
      { key: 'description', label: 'Description', placeholder: '...' },
    ],
    newItem: () => ({ icon: '', title: '', description: '' }),
  },
  cards: {
    fields: [
      { key: 'title', label: 'Title', placeholder: 'Card title' },
      { key: 'description', label: 'Description', placeholder: '...' },
      { key: 'imageUrl', label: 'Image URL', placeholder: 'https://...' },
      { key: 'link', label: 'Link', placeholder: '/page' },
    ],
    newItem: () => ({ title: '', description: '' }),
  },
};

// ─── CollectionSelectField component ────────────────────────────────────────────

type CollectionPickerItem = {
  id: string;
  slug: string;
  name: string;
  fieldsJson: { key: string; label: string; type: string }[];
};

function CollectionSelectField({
  value,
  onChange,
  spaceId,
}: {
  value: string;
  onChange: (v: string) => void;
  spaceId?: string;
}) {
  const { data: collections = [], isLoading } = useQuery<CollectionPickerItem[]>({
    queryKey: ['cms-collections-picker', spaceId],
    queryFn: () => api.get(`/api/v1/cms/spaces/${spaceId}/collections`).then((r) => r.data),
    enabled: !!spaceId,
    staleTime: 60_000,
  });
  return (
    <select
      className={selectCls}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={isLoading}
    >
      <option value="">{isLoading ? 'Loading…' : '— select collection —'}</option>
      {collections.map((c) => (
        <option key={c.id} value={c.slug}>
          {c.name}
        </option>
      ))}
    </select>
  );
}

// ─── FieldSelectField component ──────────────────────────────────────────────────

function FieldSelectField({
  value,
  onChange,
  spaceId,
  collectionSlug,
}: {
  value: string;
  onChange: (v: string) => void;
  spaceId?: string;
  collectionSlug: string;
}) {
  const { data: collections = [] } = useQuery<CollectionPickerItem[]>({
    queryKey: ['cms-collections-picker', spaceId],
    queryFn: () => api.get(`/api/v1/cms/spaces/${spaceId}/collections`).then((r) => r.data),
    enabled: !!spaceId,
    staleTime: 60_000,
  });
  const collection = collections.find((c) => c.slug === collectionSlug);
  const fields = collection?.fieldsJson ?? [];
  return (
    <select
      className={selectCls}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={!collectionSlug}
    >
      <option value="">
        {collectionSlug ? '— select field —' : '— select a collection first —'}
      </option>
      {fields.map((f) => (
        <option key={f.key} value={f.key}>
          {f.label} ({f.key})
        </option>
      ))}
    </select>
  );
}

// ─── ArrayItemsEditor component ──────────────────────────────────────────────────

function ArrayItemsEditor({
  fieldKey,
  label,
  value,
  onChange,
}: {
  fieldKey: string;
  label: string;
  value: unknown;
  onChange: (val: unknown) => void;
}) {
  const schema = ARRAY_ITEM_SCHEMAS[fieldKey];
  const items: Record<string, unknown>[] = Array.isArray(value) ? value : [];

  function updateItem(idx: number, key: string, val: unknown) {
    const updated = items.map((item, i) => (i === idx ? { ...item, [key]: val } : item));
    onChange(updated);
  }

  function removeItem(idx: number) {
    onChange(items.filter((_, i) => i !== idx));
  }

  function addItem() {
    onChange([...items, schema ? schema.newItem() : {}]);
  }

  function moveItem(idx: number, dir: -1 | 1) {
    const target = idx + dir;
    if (target < 0 || target >= items.length) return;
    const arr = [...items];
    [arr[idx], arr[target]] = [arr[target], arr[idx]];
    onChange(arr);
  }

  const fields = schema?.fields ?? [{ key: 'value', label: 'Value' }];

  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <label className={labelCls}>{label}</label>
        <span className="text-[9px] text-gray-500">{items.length} item{items.length !== 1 ? 's' : ''}</span>
      </div>
      <div className="space-y-1.5">
        {items.map((item, idx) => (
          <div key={idx} className="rounded-md border border-gray-300 bg-white/50 p-2">
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-[9px] text-gray-500 font-medium">#{idx + 1}</span>
              <div className="flex items-center gap-0.5">
                <button
                  type="button"
                  onClick={() => moveItem(idx, -1)}
                  disabled={idx === 0}
                  className="text-[9px] text-gray-500 hover:text-gray-700 disabled:opacity-30 px-1"
                  title="Move up"
                >▲</button>
                <button
                  type="button"
                  onClick={() => moveItem(idx, 1)}
                  disabled={idx === items.length - 1}
                  className="text-[9px] text-gray-500 hover:text-gray-700 disabled:opacity-30 px-1"
                  title="Move down"
                >▼</button>
                <button
                  type="button"
                  onClick={() => removeItem(idx)}
                  className="text-[9px] text-red-400 hover:text-red-300 px-1"
                  title="Remove"
                >✕</button>
              </div>
            </div>
            <div className="space-y-1">
              {fields.map((f) => (
                <div key={f.key}>
                  {f.type === 'select' ? (
                    <select
                      className={selectCls}
                      value={String(item[f.key] ?? '')}
                      onChange={(e) => updateItem(idx, f.key, e.target.value)}
                    >
                      <option value="">— {f.label} —</option>
                      {(f.options ?? []).map((opt) => <option key={opt} value={opt}>{opt}</option>)}
                    </select>
                  ) : (
                    <input
                      className={inputCls}
                      placeholder={f.placeholder ?? f.label}
                      value={String(item[f.key] ?? '')}
                      onChange={(e) => updateItem(idx, f.key, e.target.value)}
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
      <button
        type="button"
        onClick={addItem}
        className="mt-1.5 w-full rounded-md border border-dashed border-gray-300 hover:border-zinc-400 py-1.5 text-[10px] text-gray-600 hover:text-zinc-900 transition-colors"
      >
        + Add item
      </button>
    </div>
  );
}

// ─── Dynamic content fields ─────────────────────────────────────────────────────

function ContentFields({
  def,
  props,
  spaceId,
  contextCollectionSlug,
  onChange,
}: {
  def: BlockDef;
  props: Record<string, unknown>;
  spaceId?: string;
  contextCollectionSlug?: string;
  onChange: (props: Record<string, unknown>) => void;
}) {
  const [mediaPickerField, setMediaPickerField] = useState<string | null>(null);
  const [mediaPickerAllowedTypes, setMediaPickerAllowedTypes] = useState<string[] | undefined>(undefined);

  function set(key: string, value: unknown) {
    onChange({ ...props, [key]: value });
  }

  if (def.fieldsJson.length === 0) {
    return <p className="text-xs text-gray-500">No configurable fields for this block.</p>;
  }

  const mediaPickerModal = (
    <MediaPickerModal
      open={mediaPickerField !== null}
      onClose={() => { setMediaPickerField(null); setMediaPickerAllowedTypes(undefined); }}
      onSelect={(url: string, meta?: { srcset?: string; webpSrcset?: string; blurDataUrl?: string }) => {
        if (mediaPickerField) {
          const updated = { ...props, [mediaPickerField]: url };
          if (meta?.srcset) updated[`${mediaPickerField}Srcset`] = meta.srcset;
          if (meta?.webpSrcset) updated[`${mediaPickerField}WebpSrcset`] = meta.webpSrcset;
          if (meta?.blurDataUrl) updated[`${mediaPickerField}BlurDataUrl`] = meta.blurDataUrl;
          onChange(updated);
        }
        setMediaPickerField(null);
        setMediaPickerAllowedTypes(undefined);
      }}
      allowedTypes={mediaPickerAllowedTypes as any}
    />
  );

  return (
    <div className="space-y-3">
      {def.fieldsJson.map((field) => {
        const val = props[field.key] ?? '';
        switch (field.type) {
          case 'boolean':
            return (
              <label key={field.key} className="flex cursor-pointer items-center gap-2">
                <input
                  type="checkbox"
                  checked={Boolean(val)}
                  onChange={(e) => set(field.key, e.target.checked)}
                  className="rounded border-gray-300 bg-gray-100 text-zinc-700"
                />
                <span className="text-xs text-gray-700">{field.label}</span>
              </label>
            );
          case 'number':
            return (
              <div key={field.key}>
                <label className={labelCls}>{field.label}</label>
                <input
                  type="number"
                  className={inputCls}
                  value={String(val ?? '')}
                  onChange={(e) => set(field.key, parseFloat(e.target.value))}
                />
              </div>
            );
          case 'select':
            return (
              <div key={field.key}>
                <label className={labelCls}>{field.label}</label>
                <select
                  className={selectCls}
                  value={String(val ?? '')}
                  onChange={(e) => set(field.key, e.target.value)}
                >
                  <option value="">— select —</option>
                  {(field.options ?? []).map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
              </div>
            );
          case 'color':
            return (
              <div key={field.key}>
                <label className={labelCls}>{field.label}</label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={String(val || '#000000')}
                    onChange={(e) => set(field.key, e.target.value)}
                    className="h-7 w-10 cursor-pointer rounded border border-gray-300 bg-transparent"
                  />
                  <input
                    className={cn(inputCls, 'flex-1')}
                    value={String(val ?? '')}
                    onChange={(e) => set(field.key, e.target.value)}
                    placeholder="#000000"
                  />
                </div>
              </div>
            );
          case 'rich_text':
            return (
              <div key={field.key}>
                <label className={labelCls}>{field.label}</label>
                <SimpleRichTextEditor
                  value={String(val ?? '')}
                  onChange={(html) => set(field.key, html)}
                  placeholder={`Enter ${field.label.toLowerCase()}…`}
                />
              </div>
            );
          case 'image_url':
          case 'image':
          case 'media_url': {
            const isMediaUrl = field.type === 'media_url';
            const isImageField = !isMediaUrl;
            return (
              <div key={field.key}>
                <label className={labelCls}>{field.label}</label>
                {val && typeof val === 'string' && isImageField && (
                  <div className="mb-1.5 rounded-md overflow-hidden border border-gray-300 bg-white">
                    <img src={val} alt="" className="w-full h-24 object-cover" />
                  </div>
                )}
                {val && typeof val === 'string' && isMediaUrl && (
                  <div className="mb-1.5 px-2 py-1.5 rounded-md border border-gray-300 bg-white text-[10px] text-gray-600 truncate">
                    {val}
                  </div>
                )}
                <div className="flex gap-1.5">
                  <input
                    className={cn(inputCls, 'flex-1')}
                    value={String(val ?? '')}
                    onChange={(e) => set(field.key, e.target.value)}
                    placeholder="https://..."
                  />
                  <button
                    type="button"
                    onClick={() => {
                      setMediaPickerField(field.key);
                      setMediaPickerAllowedTypes(isMediaUrl ? ['video', 'embed'] : ['image']);
                    }}
                    className="shrink-0 flex items-center gap-1 px-2 py-1.5 rounded-md bg-zinc-900 hover:bg-zinc-800 text-white text-[10px] font-medium transition-colors"
                  >
                    <ImageIcon size={12} />
                    Browse
                  </button>
                </div>
              </div>
            );
          }
          case 'collection_select':
            return (
              <div key={field.key}>
                <label className={labelCls}>{field.label}</label>
                <CollectionSelectField
                  value={String(val ?? '')}
                  onChange={(v) => set(field.key, v)}
                  spaceId={spaceId}
                />
              </div>
            );
          case 'field_select':
            return (
              <div key={field.key}>
                <label className={labelCls}>{field.label}</label>
                <FieldSelectField
                  value={String(val ?? '')}
                  onChange={(v) => set(field.key, v)}
                  spaceId={spaceId}
                  collectionSlug={String(props[field.collectionKey ?? ''] ?? '') || (contextCollectionSlug ?? '')}
                />
              </div>
            );
          case 'json':
            return (
              <ArrayItemsEditor
                key={field.key}
                fieldKey={field.key}
                label={field.label}
                value={val}
                onChange={(v) => set(field.key, v)}
              />
            );
          default:
            return (
              <div key={field.key}>
                <label className={labelCls}>{field.label}</label>
                <div className="flex gap-1">
                  <input
                    className={cn(inputCls, 'flex-1')}
                    value={String(val ?? '')}
                    onChange={(e) => set(field.key, e.target.value)}
                  />
                  <VariablePicker
                    onInsert={(tag) => {
                      const cur = String(val ?? '');
                      set(field.key, cur + tag);
                    }}
                  />
                </div>
              </div>
            );
        }
      })}
      {mediaPickerModal}
    </div>
  );
}

// ─── Style tab ──────────────────────────────────────────────────────────────────

function StyleFields({
  props,
  onChange,
}: {
  props: Record<string, unknown>;
  onChange: (props: Record<string, unknown>) => void;
}) {
  const style = (props.styleProps ?? {}) as StyleProps;

  function setStyle(key: keyof StyleProps, value: unknown) {
    onChange({ ...props, styleProps: { ...style, [key]: value } });
  }

  return (
    <div className="space-y-3">
      <div>
        <label className={labelCls}>Padding top</label>
        <select
          className={selectCls}
          value={style.paddingTop ?? 'none'}
          onChange={(e) => setStyle('paddingTop', e.target.value)}
        >
          {PADDING_OPTIONS.map((o) => (
            <option key={o} value={o}>{o}</option>
          ))}
        </select>
      </div>
      <div>
        <label className={labelCls}>Padding bottom</label>
        <select
          className={selectCls}
          value={style.paddingBottom ?? 'none'}
          onChange={(e) => setStyle('paddingBottom', e.target.value)}
        >
          {PADDING_OPTIONS.map((o) => (
            <option key={o} value={o}>{o}</option>
          ))}
        </select>
      </div>
      <div>
        <label className={labelCls}>Background color</label>
        <div className="flex items-center gap-2">
          <input
            type="color"
            value={String(style.backgroundColor || '#ffffff')}
            onChange={(e) => setStyle('backgroundColor', e.target.value)}
            className="h-7 w-10 cursor-pointer rounded border border-gray-300 bg-transparent"
          />
          <input
            className={cn(inputCls, 'flex-1')}
            value={String(style.backgroundColor ?? '')}
            onChange={(e) => setStyle('backgroundColor', e.target.value)}
            placeholder="#ffffff or transparent"
          />
        </div>
      </div>
      <div>
        <label className={labelCls}>Text color</label>
        <div className="flex items-center gap-2">
          <input
            type="color"
            value={String(style.textColor || '#000000')}
            onChange={(e) => setStyle('textColor', e.target.value)}
            className="h-7 w-10 cursor-pointer rounded border border-gray-300 bg-transparent"
          />
          <input
            className={cn(inputCls, 'flex-1')}
            value={String(style.textColor ?? '')}
            onChange={(e) => setStyle('textColor', e.target.value)}
            placeholder="#000000"
          />
        </div>
      </div>
      <div>
        <label className={labelCls}>Max width</label>
        <select
          className={selectCls}
          value={style.maxWidth ?? 'full'}
          onChange={(e) => setStyle('maxWidth', e.target.value)}
        >
          {MAX_WIDTH_OPTIONS.map((o) => (
            <option key={o} value={o}>{o}</option>
          ))}
        </select>
      </div>
    </div>
  );
}

// ─── Panel ─────────────────────────────────────────────────────────────────────

export function BlockPropsPanel({
  blockId,
  blockType,
  blockDef,
  props,
  spaceId,
  contextCollectionSlug,
  onUpdate,
  onDelete,
  onClose,
}: Props) {
  const [tab, setTab] = useState<Tab>('content');

  const TABS: { id: Tab; label: string }[] = [
    { id: 'content', label: 'Contenuto' },
    { id: 'style', label: 'Stile' },
    { id: 'advanced', label: 'Avanzato' },
  ];

  return (
    <div className="flex w-64 shrink-0 flex-col border-l border-gray-200 bg-white">
      {/* Header */}
      <div className="flex items-start justify-between border-b border-gray-200 px-3 py-2.5">
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-wider text-zinc-900">
            {blockDef?.name ?? blockType.replace(/_/g, ' ')}
          </div>
          <div className="mt-0.5 text-[10px] text-gray-500">Modifica proprietà</div>
        </div>
        <div className="flex items-center gap-1">
          <button
            title="Delete block"
            onClick={() => onDelete(blockId)}
            className="rounded p-1 text-gray-500 hover:bg-red-500/20 hover:text-red-600 transition-colors"
          >
            <Trash2 size={12} />
          </button>
          <button
            title="Close panel"
            onClick={onClose}
            className="rounded p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition-colors"
          >
            <X size={12} />
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-gray-200">
        {TABS.map(({ id, label }) => (
          <button
            key={id}
            onClick={() => setTab(id)}
            className={cn(
              'flex-1 py-2 text-[11px] font-medium transition-colors',
              tab === id
                ? 'border-b-2 border-zinc-900 text-zinc-900'
                : 'text-gray-500 hover:text-gray-700',
            )}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Field area */}
      <div className="flex-1 overflow-y-auto p-3">
        {tab === 'content' && (
          <>
            {blockDef ? (
              <ContentFields
                def={blockDef}
                props={props}
                spaceId={spaceId}
                contextCollectionSlug={contextCollectionSlug}
                onChange={(newProps) => onUpdate(blockId, newProps)}
              />
            ) : (
              <p className="text-xs text-gray-500">
                Block definition not found for type &quot;{blockType}&quot;.
              </p>
            )}
          </>
        )}
        {tab === 'style' && (
          <StyleFields
            props={props}
            onChange={(newProps) => onUpdate(blockId, newProps)}
          />
        )}
        {tab === 'advanced' && (
          <div className="space-y-3">
            <div>
              <label className={labelCls}>Block ID</label>
              <p className="rounded bg-white px-2 py-1.5 font-mono text-[10px] text-gray-600 break-all">
                {blockId}
              </p>
            </div>
            <div>
              <label className={labelCls}>Block type</label>
              <p className="rounded bg-white px-2 py-1.5 font-mono text-[10px] text-gray-600">
                {blockType}
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
